import React from 'react';
import { FaLock, FaStripe, FaShieldAlt } from 'react-icons/fa';
import { AiFillSafetyCertificate } from 'react-icons/ai';
import { BsShieldFillCheck } from 'react-icons/bs';
import { RiSecurePaymentLine } from 'react-icons/ri';
// Official full-color card-network artwork (local SVGs, no external CDN).
import Visa from 'react-payment-logos/dist/flat/Visa';
import Mastercard from 'react-payment-logos/dist/flat/Mastercard';
import Amex from 'react-payment-logos/dist/flat/Amex';
import Discover from 'react-payment-logos/dist/flat/Discover';

/**
 * SecurePaymentBadges — site-wide payment trust section, shown above the footer.
 *
 * Styled to match the 12and22 marketing language: a centered orange/yellow
 * eyebrow pill + heading, soft white rounded-2xl cards on a light ground, brand
 * navy/amber accents — no heavy navy header bar or hard dividers. Card-brand
 * logos keep their own recognisable colors.
 */
const SecurePaymentBadges: React.FC = () => {
  return (
    <section aria-label="Payment security and accepted methods" className="text-center">
      {/* Eyebrow pill — matches the site's section eyebrows */}
      <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
        <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-2.5"></span>
        <span className="text-sm font-medium text-gray-700">Secure &amp; encrypted checkout</span>
      </div>
      <p className="text-sm text-gray-500 max-w-xl mx-auto mb-8">
        Payments are processed by Stripe with bank-grade encryption. We never see or store your card details.
      </p>

      {/* Certification cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
        <TrustCard
          icon={<FaStripe className="text-4xl text-[#635bff]" aria-hidden />}
          title="Powered by Stripe"
          sub="Trusted by millions of businesses"
        />
        <TrustCard
          icon={<BsShieldFillCheck className="text-3xl text-primary-navy" aria-hidden />}
          title="PCI DSS Level 1"
          sub="Highest payment-security certification"
        />
        <TrustCard
          icon={<AiFillSafetyCertificate className="text-3xl text-secondary-500" aria-hidden />}
          title="256-bit SSL"
          sub="Bank-grade encryption in transit"
        />
      </div>

      {/* Accepted cards + guarantees, on one soft row */}
      <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-4">
        <div className="flex items-center gap-2.5">
          <CardTile aria-label="Visa"><Visa className="h-6 w-auto" /></CardTile>
          <CardTile aria-label="Mastercard"><Mastercard className="h-6 w-auto" /></CardTile>
          <CardTile aria-label="American Express"><Amex className="h-6 w-auto" /></CardTile>
          <CardTile aria-label="Discover"><Discover className="h-6 w-auto" /></CardTile>
          <span className="text-xs text-gray-400">&amp; more</span>
        </div>
        <div className="hidden sm:block w-px h-6 bg-gray-200" />
        <div className="flex items-center flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-500">
          <Chip icon={<FaShieldAlt aria-hidden />} label="Fraud protection" />
          <Chip icon={<RiSecurePaymentLine aria-hidden />} label="Secure checkout" />
          <Chip icon={<FaLock aria-hidden />} label="Data encryption" />
        </div>
      </div>
    </section>
  );
};

const TrustCard: React.FC<{ icon: React.ReactNode; title: string; sub: string }> = ({ icon, title, sub }) => (
  <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-5 flex items-center gap-3 text-left hover:shadow-lg transition-shadow duration-300">
    <span className="shrink-0">{icon}</span>
    <div>
      <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{sub}</p>
    </div>
  </div>
);

const CardTile: React.FC<{ children: React.ReactNode; 'aria-label'?: string }> = ({ children, ...rest }) => (
  <span
    role="img"
    {...rest}
    className="inline-flex items-center justify-center h-9 min-w-[52px] px-2.5 bg-white border border-gray-200 rounded-lg shadow-sm"
  >
    {children}
  </span>
);

const Chip: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1.5 font-medium">
    <span className="text-secondary-600">{icon}</span>
    {label}
  </span>
);

export default SecurePaymentBadges;
